/**
 * W-1 登录 / 注册(FR-1)。视觉真源 design/login.html。
 * 手机号 + 短信验证码,登录/注册合一(未注册自动建号,010107 → register)。
 * 五态:默认 / 加载中 / 错误(留屏红字)/ 边界(协议未勾选拦截 / 已登录重定向)。
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as Checkbox from '@radix-ui/react-checkbox';
import { loginOrRegister, sendSmsCode, type TokenPairResponse } from '@/api/auth';
import { ServerError } from '@/api/client';
import { useAuthStore } from '@/store/auth';

const PHONE_RE = /^1[3-9]\d{9}$/;
const COUNTDOWN_SECONDS = 60;

interface FromState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  // 会话过期回跳(client.ts 401 → /login?expired=1):初始即提示。
  const expired = new URLSearchParams(location.search).get('expired') === '1';

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(true); // 按 design 默认勾选
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(expired ? '登录已过期,请重新登录' : null);
  const submitLock = useRef(false);
  const sendLock = useRef(false);

  const normalizedPhone = phone.replace(/\s/g, '');
  const phoneValid = PHONE_RE.test(normalizedPhone);
  const codeValid = /^\d{6}$/.test(code);
  const from = (location.state as FromState | null)?.from;

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // 已持有效会话访问登录页 → 直接重定向(边界用例)
  if (isAuthenticated) {
    return <Navigate to={from ?? '/'} replace />;
  }

  const onSendCode = async () => {
    setError(null);
    if (!phoneValid) {
      setError('请输入正确的手机号');
      return;
    }
    if (sendLock.current) return; // 互斥防双击(与 onSubmit 对称)
    sendLock.current = true;
    setSending(true);
    try {
      const resp = await sendSmsCode(normalizedPhone, 'login');
      setCountdown(resp.cooldownSec ?? COUNTDOWN_SECONDS);
    } catch (err) {
      setError(toMessage(err, '验证码发送失败,请稍后重试'));
    } finally {
      setSending(false);
      sendLock.current = false;
    }
  };

  const onSubmit = async () => {
    setError(null);
    if (!agreed) {
      setError('请先阅读并同意《用户协议》与《隐私政策》');
      return;
    }
    if (!phoneValid) {
      setError('请输入正确的手机号');
      return;
    }
    if (!codeValid) {
      setError('请输入 6 位验证码');
      return;
    }
    if (submitLock.current) return; // 互斥防双击
    submitLock.current = true;
    setSubmitting(true);
    try {
      const token = await loginOrRegister(normalizedPhone, code);
      // 防御:成功 envelope 但缺 token/有效期(异常响应)→ 当失败处理,不静默"假登录"。
      if (!token.accessToken || !token.accessTokenExpiresIn) {
        throw new Error('登录失败,请稍后重试');
      }
      persistSession(setSession, token);
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      setError(toMessage(err, '登录失败,请稍后重试'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const sendDisabled = sending || countdown > 0 || !phoneValid;
  const submitDisabled = submitting || !phoneValid || !codeValid;
  const sendLabel = sending ? '发送中…' : countdown > 0 ? `${countdown}s 后重试` : '获取验证码';

  return (
    <div className="page">
      {/* 极简顶栏:品牌 + 先随便逛逛(登录前不渲染全站主导航) */}
      <header className="header">
        <div className="header-inner">
          <Link className="brand" to="/">
            我行
          </Link>
          <div className="header-actions">
            <Link className="muted sm" to="/">
              先随便逛逛 →
            </Link>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="auth-wrap">
          <div className="auth-card">
            <div className="col" style={{ alignItems: 'center', marginBottom: 'var(--space-8)' }}>
              <div className="brand" style={{ fontSize: 'var(--text-3xl-size)' }}>
                我行
              </div>
              <p className="muted sm mt-2">登录后开始浏览、下单与沟通</p>
            </div>

            <div className="card card-pad">
              <div className="field">
                <label className="label" htmlFor="login-phone">
                  手机号
                </label>
                <input
                  id="login-phone"
                  className="input"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label" htmlFor="login-code">
                  短信验证码
                </label>
                <div className="row gap-2">
                  <input
                    id="login-code"
                    className="input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6 位验证码"
                    style={{ flex: 1 }}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                    disabled={sendDisabled}
                    onClick={onSendCode}
                  >
                    {sendLabel}
                  </button>
                </div>
              </div>

              {error && (
                <p className="sm" role="alert" style={{ color: 'var(--color-destructive)' }}>
                  {error}
                </p>
              )}

              <button
                type="button"
                className="btn btn-primary btn-lg btn-block mt-4"
                disabled={submitDisabled}
                onClick={onSubmit}
              >
                {submitting ? '登录中…' : '登录 / 注册'}
              </button>

              <label
                className="row gap-2 mt-6"
                style={{ alignItems: 'flex-start', cursor: 'pointer' }}
              >
                <Checkbox.Root
                  className="checkbox-root"
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(v === true)}
                  style={{ marginTop: 3 }}
                >
                  <Checkbox.Indicator className="checkbox-indicator">✓</Checkbox.Indicator>
                </Checkbox.Root>
                <span className="xs muted">
                  已阅读并同意{' '}
                  {/* 新标签打开:阅读协议不丢失登录卡已填手机号/验证码与倒计时 */}
                  <Link
                    to="/agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    《用户协议》
                  </Link>{' '}
                  与{' '}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    《隐私政策》
                  </Link>
                  。未注册的手机号将自动创建账号。
                </span>
              </label>
            </div>

            <p className="xs muted mt-6" style={{ textAlign: 'center' }}>
              遇到问题?联系客服{' '}
              <a href="mailto:support@tianhengjinyuan.com">support@tianhengjinyuan.com</a>
              {' · '}
              <a href="tel:19902065846">19902065846</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function persistSession(
  setSession: ReturnType<typeof useAuthStore.getState>['setSession'],
  token: TokenPairResponse,
) {
  setSession({
    accessToken: token.accessToken ?? '',
    refreshToken: token.refreshToken ?? '',
    accessTokenExpiresAt: token.accessTokenExpiresIn
      ? Date.now() + token.accessTokenExpiresIn * 1000
      : 0,
    userId: token.userId ?? '',
    identityState: token.identityState ?? null,
  });
}

function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ServerError) return err.message || fallback;
  return fallback;
}
